import { FileCheck } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSetSnapshot } from '@/hooks/use-set-snapshot';
import { filterPresentaciones, formatPeriodo } from '@/components/marangatu-snapshot-view';

/**
 * Últimas presentaciones del tenant ante la SET (del snapshot de Marangatu),
 * filtradas por formulario. Read-only. Se oculta si no hay presentaciones del
 * tipo pedido (no ensucia la página). Pensado para las páginas IVA (F.120) y
 * Renta (F.515 / F.501).
 */
export function SetPresentacionesCard({
  formularios,
  title = 'Últimas presentaciones ante la SET',
}: {
  formularios: number[];
  title?: string;
}): React.ReactElement | null {
  const { data } = useSetSnapshot();
  const snap = data?.snapshot ?? null;
  const decls = (
    snap && snap.status !== 'error' ? filterPresentaciones(snap.ultimasDeclaraciones, formularios) : []
  )
    .slice()
    .sort((a, b) => b.periodo - a.periodo);

  if (decls.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileCheck className="size-4" />
          {title}
        </CardTitle>
        <CardDescription>
          {decls.length} presentacion{decls.length === 1 ? '' : 'es'} registrada{decls.length === 1 ? '' : 's'} en tu cuenta de Marangatu.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg border border-border">
          {decls.map((d, i) => (
            <li key={`${d.formulario}-${d.periodo}-${i}`} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
              <div className="min-w-0">
                <span className="font-medium tabular-nums">{formatPeriodo(d.periodo)}</span>
                {d.fechaPresentacion && (
                  <span className="ml-2 text-xs text-muted-foreground">presentada {d.fechaPresentacion}</span>
                )}
              </div>
              {d.estado && (
                <span className="shrink-0 text-xs text-muted-foreground">{d.estado}</span>
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
